import { Component, OnDestroy, OnInit } from '@angular/core';
import { CartItem, ProductCartService } from './product-cart-service';
import { CommonModule } from '@angular/common';
import { Subject, takeUntil } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { ProductCartDeleteDialog } from './product-cart-delete-dialog/product-cart-delete-dialog';
import { SaveOrderDialog } from '../save-order-dialog/save-order-dialog';
import { OrderService } from '../order-service';
import { SpinnerOverlayService } from '../../Components/spinner/spinner-overlay.service';
import { ActivatedRoute, Router } from '@angular/router';
import { AccountService } from '../../login/account.service';
import { MessageService } from '../../shared/message.service';
import { CustomerService } from '../../shared/service/customer-service';
import { ProductCatalogComponent } from '../product-catalog/product-catalog.component';
import { ProductDetailDialog } from '../product-detail-dialog/product-detail-dialog';
import { IProduct } from '../product-catalog/product.model';

@Component({
  selector: 'app-product-cart-component',
  imports: [CommonModule],
  templateUrl: './product-cart-component.html',
  styleUrl: './product-cart-component.css'
})
export class ProductCartComponent implements OnInit, OnDestroy {
  creditLimit: number = 0
  private destroy$ = new Subject<void>();
  cartItems: CartItem[] = [];
  productList: IProduct[] = []

  total = 0;
  isRelatedView = false;

  constructor(
    private productCartService: ProductCartService,
    private dialog: MatDialog,
    private orderService: OrderService,
    private spinner: SpinnerOverlayService,
    private router: Router,
    private accountService: AccountService,
    private messageService: MessageService,
    private customerService: CustomerService,
    private route: ActivatedRoute
  ) {
  }

  ngOnInit() {
    this.productCartService.cartItems$
      .pipe(takeUntil(this.destroy$))
      .subscribe(items => {
        this.cartItems = items;
        this.total = this.cartItems.reduce((s, it) => s + it.price * it.quantity, 0);
      });

    this.productCartService.refreshCartFromApi().subscribe();

    const addressNumber = this.accountService.userIdentity()?.user.addressNumber;
    if (addressNumber) {
      this.getCreditTermsByAddressNumber();
    }

    this.route.queryParams
      .pipe(takeUntil(this.destroy$))
      .subscribe(params => {
        const isRelated = params['related'] === 'true' || params['related'] === true;
        if (isRelated && this.cartItems.length === 0) {
          this.isRelatedView = false;
          this.router.navigate([], { queryParams: { related: null }, queryParamsHandling: 'merge' });
        } else {
          this.isRelatedView = isRelated;
        }
      });

  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  removeItemFromTheCart(itemId: string, itemName: string) {
    const dialogRef = this.dialog.open(ProductCartDeleteDialog, {
      data: {
        id: itemId,
        name: itemName
      },
      panelClass: 'lexend-dialog',
      disableClose: true
    });

    dialogRef.afterClosed().subscribe((result: any) => {
      if (result) {
        this.productCartService.removeItem(itemId);
      }
    });
  }

  handleLoading(loading: boolean) {
    if (loading) this.spinner.show(); else this.spinner.hide()
  }

  saveAsDraft() {
    const dialogRef = this.dialog.open(SaveOrderDialog, {
      panelClass: 'lexend-dialog',
      disableClose: true
    });

    dialogRef.afterClosed().subscribe((result: any) => {
      if (result) {
        this.saveOrderAsDraft();
      }
    });
  }

  proceed() {
    if (this.isRelatedView) {
      this.router.navigate(['/home/pedidos/crear/confirmar-pedido'], { queryParamsHandling: 'preserve' });
    } else {
      this.showRelatedProducts();
    }
  }

  private saveOrderAsDraft() {
    this.handleLoading(true)

    const itemsCodeList = this.cartItems.map(item => ({
      id: item.id,
      quantity: item.quantity,
      platformCount: item.platformCount,
      halfPlatformCount: item.halfPlatformCount,
      quarterPlatformCount: item.quarterPlatformCount,
      eighthPlatformCount: item.eighthPlatformCount
    }))

    if (itemsCodeList.length === 0) {
      this.messageService.showError("No hay ítems para guardar al pedido")
      return
    }

    const userID = this.accountService.userIdentity()?.user.id
    const addressNumber = this.accountService.userIdentity()?.user.addressNumber

    if (!userID || !addressNumber) {
      this.messageService.showError("No se pudo cargar la información del usuario. Por favor, recargue la página y vuelva a intentarlo")
      return
    }

    const body = {
      userID: userID!!,
      addressNumber: addressNumber!!,
      itemsCodeList: itemsCodeList!!
    }

    // include orderId from localStorage if present
    const storedOrderId = localStorage.getItem('orderId');
    const orderID = storedOrderId ? Number(storedOrderId) : null;
    if (orderID) (body as any).orderID = orderID;

    this.orderService.saveAsDraft(body).subscribe({
      next: (response: any) => {
        this.handleLoading(false)

        // try to persist authoritative orderId returned by the server
        const returnedOrderId = response?.orderId ?? response?.order?.order?.[0]?.id ?? null;
        if (returnedOrderId) localStorage.setItem('orderId', String(returnedOrderId));

        this.productCartService.cleanCartAfterSave();

        this.router.navigate(['/home/pedidos']);
      }, error: () => {
        this.handleLoading(false)
      }
    })
  }

  getCreditTermsByAddressNumber() {
    this.handleLoading(true);

    const addressNumber = this.accountService.userIdentity()?.user.addressNumber

    this.customerService.getCreditTermsByAddressNumber(addressNumber).subscribe({
      next: (response) => {
        this.creditLimit = response.body?.customer.creditLimit ?? 0;
        this.handleLoading(false);
      },
      error: () => {
        this.handleLoading(false);
      }
    })

  }

  showRelatedProducts() {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { related: true },
      queryParamsHandling: 'merge',
    });
  }

  openProductDetailDialog(productOrPayload: IProduct | number | any) {
    let dataPayload: any = null;

    if (!productOrPayload) {
      this.messageService.showError('Producto inválido');
      return;
    }

    if (typeof productOrPayload === 'number') {
      dataPayload = { id: productOrPayload };
    }
    else if ((productOrPayload as IProduct).id && !(productOrPayload as any).preselected) {
      dataPayload = { id: (productOrPayload as IProduct).id };
    }
    else if ((productOrPayload as any).id) {
      dataPayload = productOrPayload;
    } else {
      this.messageService.showError("Error: El producto no cuenta con un identificador");
      return;
    }

    const dialogRef = this.dialog.open(ProductDetailDialog, {
      panelClass: 'lexend-dialog',
      data: dataPayload,
      width: '900px',
      maxWidth: '90vw',
      maxHeight: '90vh',
      disableClose: true
    });

    const comp = dialogRef.componentInstance as ProductDetailDialog;
    if (comp?.saved) {
      comp.saved.pipe(takeUntil(this.destroy$)).subscribe((result: any) => {
        if (!result) return;

        if (result.action !== 'save') return;

        if (result.cartItemId && result.updated) {
          this.productCartService.updateItemById(result.cartItemId, result.updated as Partial<CartItem>);
          return;
        }

      });
    }

  }

  openProductDetailByCartItem(item: CartItem) {
    const payload = {
      id: item.productId,
      cartItemId: item.id,
      readOnly: true,
      preselected: {
        dimensions: item.dimensions,
        thickness: item.thickness,
        color: item.color,
        texture: item.texture,
        description: item.description,
        faceOne: item.faceOne,
        faceTwo: item.faceTwo,
        quantity: item.quantity,
        platformCount: item.platformCount,
        halfPlatformCount: item.halfPlatformCount,
        quarterPlatformCount: item.quarterPlatformCount,
        eighthPlatformCount: item.eighthPlatformCount
      },
      editMode: true,
      resetSaved: true
    };

    console.log('payload', payload);
    this.openProductDetailDialog(payload);
  }

  getItemCoating(item: CartItem): string {
    if (item.faceOne && !item.faceTwo) return '1 Cara';
    if (!item.faceOne && item.faceTwo) return '2 Caras';
    return 'Sin definir';
  }
}
